import {Component, effect, input, OnInit, output, signal, untracked, WritableSignal} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {EvenementService} from '../../shared/services/evenement.service';
import {AlertService, CodeDesignation, DateUtils} from 'sigale-angular';
import {TranslateService} from '@ngx-translate/core';
import {finalize} from 'rxjs/operators';
import {AuthService} from '../../shared/services/auth-service.service';
import {forkJoin} from 'rxjs';
import {TypeEvenement} from '../../shared/models/type-evenement.model';
import {PlageSelectionnee} from '../../shared/models/plage-selectionnee.model';
import {Router} from '@angular/router';
import {DateUtilsService} from '../../shared/services/date-utils.service';
import {Duree} from 'src/app/shared/models/duree.model';
import {StorageService} from '../../shared/services/storage.service';

@Component({
	selector: 'app-declarer-evenement',
	templateUrl: './declarer-evenement.component.html',
	styleUrl: './declarer-evenement.component.scss'
})
export class DeclarerEvenementComponent implements OnInit {
	readonly NOM_ALERTE_ERREUR_GENERALE = 'visualisationEvenements';

	plagesSelectionnees = input.required<PlageSelectionnee[]>();

	notifierCreationEvenement = output<void>();
	notifierCalendrierDeselectionJour = output<PlageSelectionnee>();

	loadingFormulaire: WritableSignal<boolean> = signal(false);
	loadingDeclarationEvenement: WritableSignal<boolean> = signal(false);
	afficherMessageErreurForm: WritableSignal<boolean> = signal(false);
	messageErreurForm: WritableSignal<string> = signal('');
	showConfirmationDemandeModal: WritableSignal<boolean> = signal(false);
	modeReservationActive = signal(false);

	durees: CodeDesignation[] = [];
	types: CodeDesignation[] = [];
	formDeclarerEvenement: FormGroup;
	loginUtilisateur: string;
	langueUtilisateur: string;

	constructor(
		private router: Router,
		private dateUtilsService: DateUtilsService,
		private evenementService: EvenementService,
		private alertService: AlertService,
		private translateService: TranslateService,
		private authService: AuthService,
		private storageService: StorageService) {
		effect(() => {
			const plagesSelectionnees = this.plagesSelectionnees();

			if (plagesSelectionnees.length > 0) {
				untracked(() => this.viderMessageErreurForm());
			}
		});
	}

	ngOnInit() {
		this.recupererDonneesPourFormulaire();
		const utilisateurConnecte = this.authService.getUtilisateurConnecte();
		this.loginUtilisateur = utilisateurConnecte.login;
		this.langueUtilisateur = utilisateurConnecte.langue;
	}

	private selectionnerTypeReservationBureau() {
		this.formDeclarerEvenement.get('commentaire').setValue('');
		this.formDeclarerEvenement.get('commentaire').disable();
		this.modeReservationActive.set(true);
	}

	/**
	 * Initialise le formulaire d'ajout d'évenements.
	 */
	initialiserFormulaire(): void {
		this.formDeclarerEvenement = new FormGroup({
			duree: new FormControl(this.durees[0], Validators.required),
			type: new FormControl(this.types[0], Validators.required),
			commentaire: new FormControl('', Validators.maxLength(255))
		});

		this.formDeclarerEvenement.get('type')?.valueChanges.subscribe((evenement) => {
			if (evenement.code === TypeEvenement.BUREAU) {
				this.selectionnerTypeReservationBureau();
			}
			else {
				this.formDeclarerEvenement.get('commentaire').enable();
				this.modeReservationActive.set(false);
			}
		});

		if (this.storageService.dernierTypeEvenementSelectionne) {
			this.formDeclarerEvenement.get('type').setValue(this.types.find(type => type.code === this.storageService.dernierTypeEvenementSelectionne.code));
		}
	}

	/**
	 * Notifie le parent et donc le calendrier que les jours ont bien été ajouté.
	 */
	onNotifierCreationEvenementsEffective(): void {
		// Permet de notifier le calendrier que la création a bien été effectuée.
		this.notifierCreationEvenement.emit();
		this.formDeclarerEvenement.get('commentaire').setValue('');

		this.viderMessageErreurForm();
	}

	/**
	 * Récupère les durées et les types pour le formulaire.
	 */
	recupererDonneesPourFormulaire() {
		this.loadingFormulaire.set(true);
		forkJoin([this.evenementService.recupererDureesPossibles(), this.evenementService.recupererTypesPossibles()])
			.pipe(finalize(() => this.loadingFormulaire.set(false)))
			.subscribe({
				next: ([durees, types]) => {
					this.durees = durees;
					this.types = types;

					const typeReservation = {
						code: TypeEvenement.BUREAU,
						designation: this.translateService.instant('ACCUEIL.DECLARATION_EVENEMENT.BUREAU_DESIGNATION')
					};

					this.types = [...types, typeReservation];

					this.initialiserFormulaire();
					this.determinerLabelBoutonValidation();

					// si un utilisateur ne peut que réserver des bureaux, on sélectionne automatiquement ce type
					if (this.types.length >= 1 && this.types[0].code === TypeEvenement.BUREAU) {
						this.selectionnerTypeReservationBureau();
					}
				},
				error: (erreur) => {
					this.alertService.clear(this.NOM_ALERTE_ERREUR_GENERALE);
					this.alertService.error(erreur.error, this.NOM_ALERTE_ERREUR_GENERALE);
				}
			});
	}

	/**
	 * Vide le message d'erreur qui est affiché si aucun jour n'a été sélectionné.
	 */
	viderMessageErreurForm(): void {
		this.afficherMessageErreurForm.set(false);
		this.messageErreurForm.set('');
	}

	/**
	 * Vrai si le nombre de caractères dépasse la limite maximale (255).
	 */
	estLimiteCaractereCommentaireDepassee(): boolean {
		return this.formDeclarerEvenement.get('commentaire').hasError('maxlength');
	}

	declarerEvenementOuReservation(): void {
		if (this.plagesSelectionnees().length === 0) {
			this.afficherMessageErreurForm.set(true);
			this.messageErreurForm.set(this.translateService.instant('ACCUEIL.DECLARATION_EVENEMENT.ERREUR.AUCUN_JOUR_SELECTIONNE'));
			return;
		}

		if (this.modeReservationActive()) {
			this.ouvrirPageReservation();
			return;
		}

		this.ouvrirModaleRecapitulatifCreationEvenements();
	}

	private ouvrirPageReservation(): void {
		if (this.plagesSelectionnees().length > 1 || !DateUtils.isDayBefore(this.plagesSelectionnees()[0].dateSelectionnee, this.dateUtilsService.recupererLeLundiDansDeuxSemaines())) {
			this.afficherMessageErreurForm.set(true);
			this.messageErreurForm.set(this.translateService.instant('ACCUEIL.DECLARATION_EVENEMENT.ERREUR.RESERVATION_IMPOSSIBLE'));
			return;
		}

		const dateReservation = this.plagesSelectionnees()[0].dateSelectionnee;
		const langue = this.authService.getUtilisateurConnecte().langue;

		// Si la durée est différente de journée alors ça veut dire quo'on pose obligatoirement pour une matinée ou une après-midi car il y a un évènement sur une demi-journée.
		const duree = this.plagesSelectionnees()[0].duree !== Duree.JOURNEE ? this.plagesSelectionnees()[0].duree : this.formDeclarerEvenement.get('duree').value.code;

		this.router.navigate(['/reserver-un-bureau'], {
			queryParams: {
				date: this.dateUtilsService.formaterDateEnStringPourEnvoiBack(dateReservation, langue),
				duree: duree
			}
		});
	}

	/**
	 * Vérifie si des jours ont bien été sélectionné et ouvre la modale de récapitulatif.
	 */
	private ouvrirModaleRecapitulatifCreationEvenements(): void {
		if (this.estLimiteCaractereCommentaireDepassee()) {
			return;
		}

		this.showConfirmationDemandeModal.set(true);
	}

	determinerLabelBoutonValidation(): string {
		let cleTraduction = 'ACCUEIL.DECLARATION_EVENEMENT.';
		switch (this.formDeclarerEvenement.get('type').value.code) {
			case TypeEvenement.TELETRAVAIL: {
				cleTraduction += 'BUTTON_DEMANDER_TELETRAVAIL';
				break;
			}
			case TypeEvenement.TRAVAILLE_AILLEURS:
			case TypeEvenement.CONGE: {
				cleTraduction += 'BUTTON_DECLARER_ABSENCE';
				break;
			}
			case TypeEvenement.BUREAU: {
				cleTraduction += 'BTN_RESERVER_BUREAU';
				break;
			}
		}

		return this.translateService.instant(cleTraduction);
	}

	estBoutonReservationDisabled(): boolean {
		return this.modeReservationActive() &&
			this.plagesSelectionnees().length >= 1 &&
			!DateUtils.isDayBefore(this.plagesSelectionnees()[0].dateSelectionnee, this.dateUtilsService.recupererLeLundiDansDeuxSemaines());
	}

	onClickType(typeSelectionne: CodeDesignation): void {
		this.viderMessageErreurForm();
		this.storageService.dernierTypeEvenementSelectionne = typeSelectionne;
	}
}
